import { CropOriginal as CropOriginalIcon } from '@mui/icons-material';
import { Button, Grid, Typography } from '@mui/material';
import { set } from 'lodash';
import { useEffect } from 'react';
import { SidebarAccordion } from 'src/components/accordion';
import { ResourceSelectorSidebar } from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { injectDefaultFontIntoHtml } from 'src/components/widget/richTextWidget';
import { FONTS_OPTIONS } from 'src/constants';
import {
  useGetProjectConfig,
  useModal,
  useRteDefaultStyleValues,
} from 'src/hooks';
import { lang } from 'src/utils';

import { IMessageConfig, MessageConfigTypes } from '../CODForm.interface';
import { RemoveMessageTitle } from '../removeMessageTitle';
import { ConfirmOrderMessagesProps } from './ConfirmOrderMessages.interface';
import { MessageList } from './modules/MessageList';

export const ConfirmOrderMessages = (props: ConfirmOrderMessagesProps) => {
  const openBackgroundDrawer = useModal();

  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts = projectConfig?.fonts?.map((item) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const rteDefaultStyleValues = useRteDefaultStyleValues(
    props.theme,
    projectConfig,
  );

  useEffect(() => {
    const { fontFamily } = rteDefaultStyleValues;
    if (!fontFamily) return;

    let changed = false;
    const updatedMessages = props.messages.map((msg) => {
      const label = msg.iconAndText?.label;
      if (!label) return msg;
      // Seed font only — never inject color, so the label inherits the
      // section's container color via the GCF cascade (see CC-22307).
      const styled = injectDefaultFontIntoHtml(label, { fontFamily });
      if (styled === label) return msg;
      changed = true;

      return {
        ...msg,
        iconAndText: { ...msg.iconAndText, label: styled },
      } as IMessageConfig;
    });

    if (changed) {
      props.onChange(updatedMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rteDefaultStyleValues.fontFamily]);

  const handleFieldChange = (field: string) => (value: any) => {
    const newMessages = set([...props.messages], field, value);

    props.onChange(newMessages);
  };

  const handleDeleteMessage = (index: number) => {
    props.onChange(props.messages.filter((_, i) => i !== index));
  };

  const handleClickReplace = (
    enabledTypeKey: MessageConfigTypes,
    messageIndex: number,
  ) => {
    const field = `${messageIndex}.${
      enabledTypeKey === MessageConfigTypes.IMAGE ? 'image' : 'iconAndText.icon'
    }`;

    openBackgroundDrawer.onOpenModal(handleFieldChange(field));
  };

  const handleAddImage = (enabledTypeKey: MessageConfigTypes) => {
    openBackgroundDrawer.onOpenModal((value: any) => {
      const newMessage = { ...props.messageTemplate, enabledTypeKey };

      set(
        newMessage,
        enabledTypeKey === MessageConfigTypes.IMAGE
          ? 'image'
          : 'iconAndText.icon',
        value,
      );

      props.onChange([...props.messages, newMessage]);
    });
  };

  const messageItems = MessageList({
    messages: props.messages,
    handleClickReplace,
    handleMessagesUpdate: handleFieldChange,
    deleteMessage: handleDeleteMessage,
    mappedFonts,
    errors: props.errors,
    rteDefaultStyleValues,
  });

  const addMessageItem = {
    titleComponent: (
      <RemoveMessageTitle label={`Set ${(messageItems?.length || 0) + 1}`} />
    ),
    hasError: false,
    defaultExpanded: true,
    content: (
      <Grid container padding={2} gap={1} wrap="nowrap">
        <Grid item xs={5}>
          <Button
            variant="outlined"
            component="label"
            onClick={() => handleAddImage(MessageConfigTypes.IMAGE)}
            startIcon={<CropOriginalIcon />}>
            {lang.addImage}
          </Button>
        </Grid>
        <Grid item xs={7}>
          <Button
            variant="outlined"
            component="label"
            onClick={() => handleAddImage(MessageConfigTypes.ICON_AND_TEXT)}
            startIcon={<CropOriginalIcon />}>
            {lang.addIconText}
          </Button>
        </Grid>
        <Grid>
          <ResourceSelectorSidebar
            label=""
            type={ResourceType.BACKGROUND}
            openResourceSelectorDrawer={openBackgroundDrawer.open}
            onToggleResourceSelectorDrawer={openBackgroundDrawer.onToggleModal}
            onChange={openBackgroundDrawer.customFn}
          />
        </Grid>
      </Grid>
    ),
  };

  return (
    <>
      {!!props.errors && (
        <Typography color="error" variant="caption" paddingLeft={2}>
          {props.errors?.[0]?.message}
        </Typography>
      )}
      <SidebarAccordion
        items={[
          ...messageItems.map((item) => ({
            ...item,
            hasError: Boolean(item.hasError),
          })),
          addMessageItem,
        ]}
      />
    </>
  );
};
